import { CMSDataList, Policy } from "@greenflagdate/shared";
import { useReq } from "@larner.dev/use-req";
import {
  Box,
  Button,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  DialogTitle,
  Flex,
  Portal,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { cmsRequest } from "../../lib/cmsHelpers";
import { StandardMarkdown } from "../StandardMarkdown/StandardMarkdown";

interface PolicyCheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  error?: string;
}

export const PolicyCheckbox = ({
  checked,
  onChange,
  error,
}: PolicyCheckboxProps) => {
  const [visiblePolicy, setVisiblePolicy] = useState<string | null>(null);
  const [policyReq, policyReqState] = useReq<CMSDataList<Policy>>(cmsRequest);

  useEffect(() => {
    if (visiblePolicy) {
      policyReq.get(`/api/policies?filters[slug][$eq]=${visiblePolicy}`);
    }
  }, [visiblePolicy, policyReq]);

  return (
    <>
      <Flex as="label" align="center" gap="0.75rem">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          style={{
            flexShrink: 0,
            width: "1.125rem",
            height: "1.125rem",
            cursor: "pointer",
          }}
        />
        <Text fontFamily="body" fontSize="0.875rem" color="brand.green.dark">
          I agree to the{" "}
          <a
            href="#"
            onClick={(e) => {
              // The links live inside <Flex as="label"> with the checkbox.
              // Without stopPropagation the click bubbles to the label and
              // also toggles the checkbox, so opening a policy would flip
              // the user's acceptance state.
              e.preventDefault();
              e.stopPropagation();
              setVisiblePolicy("refunds");
            }}
            style={{
              textDecoration: "underline",
              fontWeight: 600,
              color: "inherit",
              cursor: "pointer",
            }}
          >
            Cancellation & Refund Policy
          </a>{" "}
          and{" "}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setVisiblePolicy("waiver");
            }}
            style={{
              textDecoration: "underline",
              fontWeight: 600,
              color: "inherit",
              cursor: "pointer",
            }}
          >
            Release of Liability
          </a>
        </Text>
      </Flex>
      {error && (
        <Text fontSize="0.875rem" color="red.500" mt="0.25rem">
          {error}
        </Text>
      )}

      <DialogRoot
        open={!!visiblePolicy}
        onOpenChange={(details) => {
          if (!details.open) {
            setVisiblePolicy(null);
          }
        }}
        size="lg"
        scrollBehavior="inside"
      >
        <Portal>
          <DialogBackdrop />
          <DialogPositioner>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {policyReqState.result?.data.at(0)?.attributes.title || ""}
                </DialogTitle>
              </DialogHeader>
              <DialogBody>
                {policyReqState.loading ? (
                  <Flex justify="center" align="center" minH="20rem">
                    <Spinner size="lg" />
                  </Flex>
                ) : (
                  !!policyReqState.result?.data.at(0)?.attributes?.content && (
                    <Box pb="1rem">
                      <StandardMarkdown
                        content={
                          policyReqState.result.data.at(0)!.attributes.content
                        }
                      />
                    </Box>
                  )
                )}
              </DialogBody>
              <DialogFooter>
                <Button
                  variant="solid"
                  colorPalette="greenDark"
                  onClick={() => setVisiblePolicy(null)}
                >
                  Close
                </Button>
              </DialogFooter>
              <DialogCloseTrigger />
            </DialogContent>
          </DialogPositioner>
        </Portal>
      </DialogRoot>
    </>
  );
};
